//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useState, useEffect, useCallback, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Container, FormControl, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
// import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
// import SimpleBar from 'simplebar-react';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import AppleIcon from '@material-ui/icons/Apple';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { getFirebaseAppleAuthProvider, getFirebaseGoogleAuthProvider, logEvent } from '../../system/firebase';
import { isOnPage } from '../../system/helpers/systemHelper';
import { validateEmail } from '../../common/emailHelper';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    PATH_AUTH,
    PATH_EMP_INVITE_HANDLER,
    PATH_SUB_PAGE_VERIFICATION_EMAIL_SENT,
    PATH_HOME,
    PATH_SUB_PAGE_TERMS,
    PATH_SUB_PAGE_PRIVACY,
    PATH_FORGOT_PASSWORD,
    QUERY_PARAM_NAME_MODE,
    QUERY_PARAM_NAME_USER_TYPE,
    QUERY_PARAM_NAME_HIDE_USER_TYPE,
} from '../../system/constants';
import {
    BASE_URL,
    AUTH_ERROR_CODE_EMAIL_ALREADY_IN_USE,
    AUTH_ERROR_MSG_DEFAULT,
    CONFIRM_DIALOG_TITLE_CREATE_ACCOUNT_DIFF_EMAIL,
    CONFIRM_DIALOG_TITLE_CREATE_JS_ACCOUNT,
    CONFIRM_DIALOG_MESSAGE_CREATE_JS_ACCOUNT,
    CONFIRM_DIALOG_MESSAGE_CREATE_ACCOUNT_DIFF_EMAIL,
    REQUIRED,
    PASSWORDS_DO_NOT_MATCH,
    ERROR_MESSAGE_WEAK_PASSWORD,
    HOMEPAGE_FAQ,
    RECAPTCHA_SITE_KEY,
    ERROR_MESSAGE_NOT_VALID_EMAIL,
    ANALYTICS_EVENT_SIGN_UP,
    ANALYTICS_EVENT_LOGIN,
    USER_TYPE_JS,
    USER_TYPE_EMP,
} from '../../common/constants';
import {
    PLACEHOLDER_FIELD_CONFIRM_PASSWORD,
    PLACEHOLDER_FIELD_EMAIL,
    PLACEHOLDER_FIELD_FIRST_NAME,
    PLACEHOLDER_FIELD_LAST_NAME,
    PLACEHOLDER_FIELD_NEW_PASSWORD,
    PLACEHOLDER_FIELD_PASSWORD,
} from '../../common/placeholderHelper';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from '../../common/services/authServices';
import { checkPasswordStrength } from '../../common/authHelper';
import { useQuery } from '../../common/hooks/useQuery';
import { objectToGetParams } from '../../common/linkHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdTextField from '../../common/elemental/AfitdTextField';
// import fbIcon from '../../images/Modal/fb-icon.png';
// import twitterIcon from '../../images/Modal/twitter-fillicon.png';
import googleIcon from '../../images/Modal/google-icon.png';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _toLower from 'lodash/toLower';
import _forEach from 'lodash/forEach';
import _omit from 'lodash/omit';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { formik, isLoginMode } = params;
    const textFieldStyles = { marginTop: '10px', marginBottom: '30px' };
    const continueButtonLabelStyles = { fontSize: 16, fontWeight: 700, color: 'white' };
    const submitButtonContainerStyles = { marginTop: 38 };
    const submitButtonStyles = { textTransform: 'capitalize', width: '100%', padding: 17, fontSize: 18, fontWeight: 500 };

    return makeStyles(theme => ({
        root: {},
        titleText: { flex: 1, fontSize: 26, fontWeight: 'bold', textAlign: 'center' },
        titleContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: '600',
        },
        closeIconButton: {
            width: '20px',
            height: '20px',
        },
        paperScrollPaper: {
            // overflowY: 'auto', //'visible', //'hidden',
        },
        dialogContent: {
            // overflowY: 'hidden', //'visible',
            '&::-webkit-scrollbar-track': {
                // '-webkit-box-shadow': 'inset 0 0 1px rgba(0,0,0,0.1)',
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                // '-webkit-box-shadow': 'inset 0 0 1px rgba(0,0,0,0.1)',
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
            [theme.breakpoints.down('sm')]: { padding: '8px 16px' },
        },
        actionContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '640px',
            padding: 16,
        },
        rightActionContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            flexGrow: '2',
            marginRight: 10,
        },

        modalContent: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'white',
            // width: '554px',
            // marginTop: 110,
            borderRadius: '10px',
            // [theme.breakpoints.down('sm')]: { width: '100%' },
            // paddingLeft: 16,
            // paddingRight: 16,
        },
        closeIconContainer: {
            textAlign: 'right',
            marginTop: 29,
            marginRight: 29,
            marginBottom: 9,
            color: 'rgba(17, 30, 20, 0.4)',
            [theme.breakpoints.down('sm')]: { marginTop: 16, marginRight: 0 },
        },
        closeIconContent: { cursor: 'pointer' },
        logInHeading: { fontSize: 26, fontWeight: 'bold', textAlign: 'center' },

        appleButton: {
            width: '410px',
            [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 },
            background: '#000',
            '&:hover': {
                background: '#222',
            },
            margin: '8px 0px',
            paddingTop: '15px',
            paddingBottom: '15px',
            border: '1px solid #000',
        },
        appleIcon: { color: '#fff', width: 30, height: 30, marginRight: 75, [theme.breakpoints.down('sm')]: { marginRight: 0 } },
        appleButtonLabelText: { ...continueButtonLabelStyles, padding: '0 95px 0 0', [theme.breakpoints.down('sm')]: { padding: '0 10px 0 0' }, color: '#fff' },

        facebookButton: {
            width: '410px',
            [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 },
            background: 'rgba(57, 117, 234, 1)',
            '&:hover': {
                background: 'rgba(57, 117, 234, 1)',
            },
            margin: '8px 0px',
            paddingTop: '15px',
            paddingBottom: '15px',
        },
        facebookButtonLabelText: { ...continueButtonLabelStyles, padding: '0 70px', [theme.breakpoints.down('sm')]: { padding: 0 } },

        twitterButton: {
            width: '410px',
            [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 },
            background: 'rgba(29, 161, 242, 1)',
            '&:hover': {
                background: 'rgba(29, 161, 242, 1)',
            },
            margin: '8px 0px',
            paddingTop: '15px',
            paddingBottom: '15px',
        },
        twitterButtonLabelText: { ...continueButtonLabelStyles, padding: '0 80px', [theme.breakpoints.down('sm')]: { padding: 0 } },

        googleButton: {
            width: '410px',
            [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 },
            // background: '#FFFFFF',
            // '&:hover': {
            //     background: '#FFFFFF',
            // },
            margin: '8px 0px',
            marginBottom: 24,
            paddingTop: '15px',
            paddingBottom: '15px',
            border: '1px solid #D5D7DD',
        },
        googleButtonLabelText: { ...continueButtonLabelStyles, color: 'rgba(0, 0, 0, 0.54)', padding: '0 80px', [theme.breakpoints.down('sm')]: { padding: 0 } },

        createWithEmailContainer: {
            marginTop: 10,
        },
        createWithEmailItemLeft: {
            marginBottom: 12,
            paddingLeft: 0,
            paddingRight: 0,
            color: 'rgba(240, 242, 246, 1)',
            [theme.breakpoints.down('sm')]: { paddingLeft: 0, paddingRight: 0 },
        },
        createWithEmailItem2: { minWidth: 136 },
        createWithEmailItemRight: {
            marginBottom: 12,
            paddingLeft: 0,
            paddingRight: 0,
            color: 'rgba(240, 242, 246, 1)',
            [theme.breakpoints.down('sm')]: { paddingLeft: 0, paddingRight: 0 },
        },

        formContainer: { paddingLeft: 10, paddingRight: 10, [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 } },
        loginForm: {
            width: '410px',
            [theme.breakpoints.down('sm')]: { width: '100%' },
            paddingLeft: 0,
            paddingRight: 0,
        },
        fieldLabel: { fontWeight: 'bold' },

        firstNameContainer: { paddingRight: '10px', [theme.breakpoints.down('sm')]: { paddingRight: 0 } },
        firstName: { ...textFieldStyles },

        lastNameContainer: { paddingLeft: '10px', [theme.breakpoints.down('sm')]: { paddingLeft: 0 } },
        lastName: { ...textFieldStyles },
        reCaptchaContainer: Boolean(formik.errors.reCaptcha) ? { border: '2px solid red', width: 'fit-content' } : { width: 'fit-content' },
        reCaptchaError: { color: '#f44336 !important', marginTop: '5px', fontSize: '0.75rem', marginLeft: '15px' },

        email: { ...textFieldStyles },
        password: { ...textFieldStyles, marginBottom: isLoginMode ? 10 : 20 },
        passwordConfirm: { ...textFieldStyles },

        loginButtonContainer: { ...submitButtonContainerStyles },
        loginButton: { ...submitButtonStyles },

        createAccountButtonContainer: { ...submitButtonContainerStyles },
        createAccountButton: { ...submitButtonStyles },

        checkBoxContainer: { marginLeft: -23 },
        radioContainer: { marginLeft: '25px' },

        privacyPolicyContainer: { maxWidth: 410, [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 }, paddingTop: 10, textAlign: 'center' },
        privacyPolicyLink: { textDecoration: 'underline' },
        createAccountLink: { fontSize: 14, fontWeight: 400, textDecoration: 'underline' },
        createWithEmailText: { fontSize: 16, fontWeight: 400, color: '#9B9E9C', marginTop: 8, marginBottom: 24, textAlign: 'center' },

        alreadyAcContainer: { maxWidth: 410, [theme.breakpoints.down('sm')]: { width: '100%', maxWidth: 316 }, marginTop: 17, marginBottom: 17, textAlign: 'center' },
        alreadyAcText: { fontSize: 16, fontWeight: 500, textDecoration: 'underline' },
        termsConditionLink: { fontSize: 14, fontWeight: 400, textDecoration: 'underline' },

        forgotPasswordLink: { marginBottom: 16, display: isLoginMode ? undefined : 'none' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isLoginMode,
        setIsLoginMode,
        formik,
        accountType,
        handleClickForgotPasswordLink,
        errorMessage,
        handleCloseErrorMessageDialog,
        confirmCreateAccountTitle,
        confirmCreateAccountMessage,
        handleCloseConfirmCreateAccountDialog,
        handleClickConfirmCreateAccountDialogYes,
        history,
        // isJobCenter,
        handleClickContinueWithGoogleButton,
        handleClickContinueWithAppleButton,
        // handleClickContinueWithFacebookButton,
        // handleClickContinueWithTwitterButton,
    } = params;
    // const {} = params;
    const { onClose } = props;
    // const cancelOnClick = _get(actionButtonDefns, 'cancel.onClick', handleClickCancelButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, onClose));
    // const clearAllButton = _get(actionButtonDefns, 'clearAll');
    // const cancelButton = _get(actionButtonDefns, 'cancel');
    // const saveButton = _get(actionButtonDefns, 'save');

    // const cancelDisabled = _get(cancelButton, 'disabled', false); //_get(cancelButton, 'disabled', !isDirty);

    const defns = {
        root: {
            disableBackdropClick: true,
            disableEscapeKeyDown: true,
            maxWidth: 'md',
        },
        closeIconButton: { onClick: onClose },

        modalContainer: { container: true, direction: 'row', justify: 'center', alignItems: 'center' },
        modalContent: { item: true },
        continueFb: { container: true, direction: 'row', justify: 'center' },
        continueFbContent: { item: true },

        appleIcon: { fontSize: 'large' },
        appleButton: {
            onClick: handleClickContinueWithAppleButton,
        },
        // facebookButton: {
        //     onClick: handleClickContinueWithFacebookButton,
        // },
        // twitterButton: {
        //     onClick: handleClickContinueWithTwitterButton,
        // },
        googleButton: {
            onClick: handleClickContinueWithGoogleButton,
        },

        createWithEmailContainer: { container: true, direction: 'row', justify: 'center', alignItems: 'center' },
        createWithEmailItemLeft: isLoginMode ? { item: true, lg: 3, md: 3, sm: 2, xs: 2 } : { item: true, lg: 2, md: 2, sm: 1, xs: 1 },
        createWithEmailItem2: isLoginMode ? { item: true, lg: 6, md: 6, sm: 8, xs: 8 } : { item: true, lg: 8, md: 8, sm: 10, xs: 10 },
        createWithEmailItemRight: isLoginMode ? { item: true, lg: 3, md: 3, sm: 2, xs: 2 } : { item: true, lg: 2, md: 2, sm: 1, xs: 1 },

        formMainGrid: { container: true, direction: 'row', justify: 'center' },
        formMainGridItem: { item: true },
        formInputContainer: { container: true },
        formInputItem: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        firstNameContainer: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        lastNameContainer: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        reCaptchaContainer: { item: true },

        loginForm: {
            onSubmit: formik.handleSubmit,
        },

        firstName: {
            placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            id: 'firstName',
            ...formik.getFieldProps('firstName'),
            error: formik.touched.firstName && !!formik.errors?.firstName,
            helperText: formik.touched.firstName && formik.errors?.firstName,
        },

        lastName: {
            placeholder: PLACEHOLDER_FIELD_LAST_NAME,
            id: 'lastName',
            ...formik.getFieldProps('lastName'),
            error: formik.touched.lastName && !!formik.errors?.lastName,
            helperText: formik.touched.lastName && formik.errors?.lastName,
        },

        email: {
            placeholder: PLACEHOLDER_FIELD_EMAIL,
            id: 'email',
            ...formik.getFieldProps('email'),
            error: formik.touched.email && !!formik.errors?.email,
            helperText: formik.touched.email && formik.errors?.email,
        },

        password: {
            placeholder: isLoginMode ? PLACEHOLDER_FIELD_PASSWORD : PLACEHOLDER_FIELD_NEW_PASSWORD,
            type: 'password',
            id: 'password',
            ...formik.getFieldProps('password'),
            error: formik.touched.password && !!formik.errors?.password,
            helperText: !isLoginMode ? ERROR_MESSAGE_WEAK_PASSWORD || formik.errors?.password : formik.errors?.password,
        },

        passwordConfirm: {
            placeholder: PLACEHOLDER_FIELD_CONFIRM_PASSWORD,
            type: 'password',
            id: 'passwordConfirm',
            ...formik.getFieldProps('passwordConfirm'),
            error: formik.touched.passwordConfirm && !!formik.errors?.passwordConfirm,
            helperText: formik.touched.passwordConfirm && formik.errors?.passwordConfirm,
        },

        reCaptcha: {
            error: !!formik.errors?.reCaptcha,
            helperText: formik.errors?.reCaptcha,
        },

        loginButton: {
            variant: 'contained',
            color: 'secondary',
            type: 'submit',
            // onClick: handleClickSignInButton,
        },

        createAccountButton: {
            variant: 'contained',
            color: 'secondary',
            type: 'submit',
            // onClick: handleClickCreateAccountButton,
        },

        userTypeJsRadioButton: {
            value: USER_TYPE_JS,
            label: 'I am a Job Seeker',
            style: { color: accountType === USER_TYPE_JS ? 'black' : 'rgba(17, 30, 20, 0.4)' },
        },
        userTypeEmpRadioButton: {
            value: USER_TYPE_EMP,
            label: 'I am an Employer/ Recruiter',
            style: { color: accountType === USER_TYPE_EMP ? 'rgba(17, 30, 20, 0.4)' : ' black' },
        },

        alreadyAcText: {
            color: 'secondary',
            onClick: () => {
                setIsLoginMode(true);
                formik.setErrors({});
            },
        },
        createAccountLink: {
            color: 'secondary',
            onClick: () => {
                setIsLoginMode(false);
                formik.setErrors({});
            },
        },
        termsConditionLink: {
            color: 'secondary',
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_TERMS}`, onClose),
        },
        privacyPolicyText: {
            variant: 'body2',
        },
        privacyPolicyLink: {
            color: 'secondary',
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_PRIVACY}`, onClose),
        },

        forgotPasswordLink: {
            color: 'secondary',
            onClick: handleClickForgotPasswordLink,
        },

        errorMessageDialog: {
            title: 'Error',
            open: !!errorMessage,
            message: errorMessage,
            onClose: handleCloseErrorMessageDialog,
        },
        confirmCreateAccountDialog: {
            title: confirmCreateAccountTitle,
            open: !!confirmCreateAccountMessage,
            message: confirmCreateAccountMessage,
            onConfirm: handleClickConfirmCreateAccountDialogYes,
            onClose: handleCloseConfirmCreateAccountDialog,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const signUpWithEmail = async ({ data, accountType, isInvitationMode, invitationData, invitationCode, history }) => {
    const firstName = _get(data, 'firstName');
    const lastName = _get(data, 'lastName');
    const email = _get(data, 'email');
    const password = _get(data, 'password');

    // alert(`${firstName}\n${lastName}\n${email}\n${password}\n`);

    try {
        await createUserWithEmailAndPassword({ email, password, firstName, lastName, isJs: accountType === USER_TYPE_JS, isInvitationMode, invitationData, invitationCode });
        history.push(`${BASE_URL}${PATH_AUTH}`, { subPageId: PATH_SUB_PAGE_VERIFICATION_EMAIL_SENT });
    } catch (error) {
        throw error;
    }
};

const validate = (isLoginMode, values) => {
    const errors = {};
    const fieldNames = ['email', 'password'];
    fieldNames.push('reCaptcha');

    // required fields for login and sign up
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
    });

    // validation for sign up
    if (!isLoginMode) {
        const fieldNames = ['firstName', 'lastName', 'passwordConfirm', 'password'];
        fieldNames.push('reCaptcha');

        // required fields
        _forEach(fieldNames, fieldName => {
            if (!_get(values, fieldName)) {
                _set(errors, fieldName, REQUIRED);
            }
        });

        if (!errors.password) {
            if (!checkPasswordStrength(_get(values, 'password'))) {
                errors.password = ERROR_MESSAGE_WEAK_PASSWORD;
            }
        }
        if (!errors.email) {
            if (!validateEmail(_get(values, 'email'))) {
                errors.email = ERROR_MESSAGE_NOT_VALID_EMAIL;
            }
        }

        // password must equal confirm password
        if (!errors.password && !errors.passwordConfirm) {
            if (values.password !== values.passwordConfirm) {
                errors.password = errors.passwordConfirm = PASSWORDS_DO_NOT_MATCH;
            }
        }
    }

    return errors;
};

const handleClickButton = (history, path, onClose) => {
    onClose();
    history.push(`${BASE_URL}${path}`);
};
// const handleCloseConfirmCloseDialog = setIsConfirmCloseDialogOpen => {
//     setIsConfirmCloseDialogOpen(false);
// };

// const handleCloseConfirmCloseDialogYes = (onClose, setIsConfirmCloseDialogOpen) => {
//     setIsConfirmCloseDialogOpen(false);
//     onClose();
// };

// const handleClickCancelButton = (isDirty, setIsConfirmCloseDialogOpen, onClose) => {
//     if (isDirty) {
//         setIsConfirmCloseDialogOpen(true);
//     } else {
//         onClose();
//     }
// };

const LoginDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const auth = _get(appContext, 'auth');
    // const { setAuthRedirectResultChecked, setAuthRedirectResult } = appContext;
    // const authUser = _get(appContext, 'authUser');
    const theme = useTheme();

    const layoutContext = useContext(LayoutContext);
    const { invitationData, invitationCode, isJobCenter } = layoutContext;
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const history = useHistory();
    const location = useLocation();
    const urlSearchParams = useQuery();
    const currentLocationPathname = _get(location, 'pathname');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isLoginMode, setIsLoginMode] = useState();
    const [accountType, setAccountType] = useState(USER_TYPE_JS); // radio group MIU
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmCreateAccountTitle, setConfirmCreateAccountTitle] = useState('');
    const [confirmCreateAccountMessage, setConfirmCreateAccountMessage] = useState('');

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            passwordConfirm: '',
            firstName: '',
            lastName: '',
            reCaptcha: '',
        },
        validate: validate.bind(null, isLoginMode),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => {
            // alert(JSON.stringify(values, null, 2));
            const email = _get(values, 'email');
            const password = _get(values, 'password');

            try {
                if (isLoginMode) {
                    await signInWithEmailAndPassword({ email, password });
                    logEvent(ANALYTICS_EVENT_LOGIN);
                } else {
                    console.log('go on');
                    let isProceed = true;

                    // if invitation mode, validate the email and account type first
                    if (isInvitationMode) {
                        if (_toLower(email) !== _toLower(inviteeEmail)) {
                            setConfirmCreateAccountTitle(CONFIRM_DIALOG_TITLE_CREATE_ACCOUNT_DIFF_EMAIL);
                            setConfirmCreateAccountMessage(CONFIRM_DIALOG_MESSAGE_CREATE_ACCOUNT_DIFF_EMAIL);
                            isProceed = false;
                        } else if (accountType !== USER_TYPE_EMP) {
                            setConfirmCreateAccountTitle(CONFIRM_DIALOG_TITLE_CREATE_JS_ACCOUNT);
                            setConfirmCreateAccountMessage(CONFIRM_DIALOG_MESSAGE_CREATE_JS_ACCOUNT);
                            isProceed = false;
                        }
                    }

                    if (isProceed) {
                        await signUpWithEmail({ data: values, accountType, isInvitationMode, invitationData, invitationCode, history });
                    }
                }
            } catch (authError) {
                const code = _get(authError, 'code');
                if (!isLoginMode && code === AUTH_ERROR_CODE_EMAIL_ALREADY_IN_USE) {
                    //TODO special prompt if email account already exists
                    setErrorMessage(_get(authError, 'displayMessage', AUTH_ERROR_MSG_DEFAULT));
                } else {
                    setErrorMessage(_get(authError, 'displayMessage', AUTH_ERROR_MSG_DEFAULT));
                }
            }
        },
    });

    const classes = useStyles({ formik, isLoginMode })();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { actionButtonDefns, dialogActions, children, noLogin, isLoginMode: isLoginModeProp = true, ...otherProps } = props;
    const { open, setContentRef, hideJS, hideEmp } = props;
    const { paperScrollPaper: paperScrollPaperCls } = classes;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const titleText = isLoginMode ? 'Account Log in' : 'Create your Account';
    const isInvitationMode = isOnPage(location, PATH_EMP_INVITE_HANDLER);
    const setFieldValue = _get(formik, 'setFieldValue');
    const inviteeEmail = _get(invitationData, 'email');

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // local login/create-account mode
    useEffect(() => {
        setIsLoginMode(isLoginModeProp);
    }, [isLoginModeProp]);

    // force account type to EMP if JS option is hidden
    useEffect(() => {
        if (hideJS) setAccountType(USER_TYPE_EMP);
    }, [hideJS]);

    // force account type to JS if EMP option is hidden
    useEffect(() => {
        if (hideEmp) setAccountType(USER_TYPE_JS);
    }, [hideEmp]);

    // for invitation mode, pre-populate email field and force EMP account type
    useEffect(() => {
        if (open && isInvitationMode && inviteeEmail && setFieldValue) {
            setFieldValue('email', inviteeEmail);
            // setIsUserTypeJsRadioSelected(false);
            setAccountType(USER_TYPE_EMP);
        }
    }, [open, isInvitationMode, inviteeEmail, setFieldValue]);

    //---------------------------------------------------------------------
    // Event Handlers and useCallback
    //---------------------------------------------------------------------
    const handleChangeAccountType = event => {
        setAccountType(event.target.value);
    };

    const handleCloseErrorMessageDialog = useCallback(() => {
        setErrorMessage('');
    }, [setErrorMessage]);

    const handleCloseConfirmCreateAccountDialog = useCallback(() => {
        setConfirmCreateAccountTitle('');
        setConfirmCreateAccountMessage('');
    }, [setConfirmCreateAccountTitle, setConfirmCreateAccountMessage]);

    const handleClickConfirmCreateAccountDialogYes = useCallback(() => {
        if (formik) {
            signUpWithEmail({ data: formik.values, accountType, isInvitationMode, history });

            logEvent(ANALYTICS_EVENT_SIGN_UP, { method: 'email', user_type: accountType });
        }
    }, [formik, accountType, isInvitationMode, history]);

    const handleClickContinueWithGoogleButton = useCallback(async () => {
        let newUrlPath = null;
        let queryParams = {};
        urlSearchParams.forEach((value, key) => (queryParams[key] = value));
        newUrlPath = `${currentLocationPathname}${objectToGetParams({
            ..._omit(queryParams, QUERY_PARAM_NAME_MODE),
            [QUERY_PARAM_NAME_USER_TYPE]: accountType,
            ...(hideEmp || hideJS ? { [QUERY_PARAM_NAME_HIDE_USER_TYPE]: 'y' } : {}),
        })}`;
        history.replace(newUrlPath);

        const provider = getFirebaseGoogleAuthProvider();
        // auth.signInWithRedirect(provider);
        await auth.signInWithPopup(provider);
        // console.log('>>>>>>>>>>>>>>>>>>>>>>>>> handleClickContinueWithGoogleButton signInWithPopup result:', result);
    }, [auth, urlSearchParams, accountType, currentLocationPathname, hideEmp, hideJS, history]);

    const handleClickContinueWithAppleButton = useCallback(async () => {
        let newUrlPath = null;
        let queryParams = {};
        urlSearchParams.forEach((value, key) => (queryParams[key] = value));
        newUrlPath = `${currentLocationPathname}${objectToGetParams({
            ..._omit(queryParams, QUERY_PARAM_NAME_MODE),
            [QUERY_PARAM_NAME_USER_TYPE]: accountType,
            ...(hideEmp || hideJS ? { [QUERY_PARAM_NAME_HIDE_USER_TYPE]: 'y' } : {}),
        })}`;
        history.replace(newUrlPath);

        const provider = getFirebaseAppleAuthProvider();
        // auth.signInWithRedirect(provider);
        await auth.signInWithPopup(provider);
        // console.log('>>>>>>>>>>>>>>>>>>>>>>>>> handleClickContinueWithGoogleButton signInWithPopup result:', result);
    }, [auth, urlSearchParams, accountType, currentLocationPathname, hideEmp, hideJS, history]);

    // const handleClickContinueWithFacebookButton = useCallback(async () => {
    //     let newUrlPath = null;
    //     let queryParams = {};
    //     urlSearchParams.forEach((value, key) => (queryParams[key] = value));
    //     newUrlPath = `${currentLocationPathname}${objectToGetParams({
    //         ..._omit(queryParams, QUERY_PARAM_NAME_MODE),
    //         [QUERY_PARAM_NAME_USER_TYPE]: accountType,
    //         ...(hideEmp || hideJS ? { [QUERY_PARAM_NAME_HIDE_USER_TYPE]: 'y' } : {}),
    //     })}`;
    //     history.replace(newUrlPath);

    //     const provider = getFirebaseFacebookAuthProvider();
    //     // auth.signInWithRedirect(provider);
    //     await auth.signInWithPopup(provider);
    //     // console.log('>>>>>>>>>>>>>>>>>>>>>>>>> handleClickContinueWithGoogleButton signInWithPopup result:', result);
    // }, [auth, urlSearchParams, accountType, currentLocationPathname, hideEmp, hideJS, history]);

    // const handleClickContinueWithTwitterButton = useCallback(() => {
    //     let newUrlPath = null;
    //     let queryParams = {};
    //     urlSearchParams.forEach((value, key) => (queryParams[key] = value));
    //     newUrlPath = `${currentLocationPathname}${objectToGetParams({
    //         ..._omit(queryParams, QUERY_PARAM_NAME_MODE),
    //         [QUERY_PARAM_NAME_USER_TYPE]: accountType,
    //         ...(hideEmp || hideJS ? { [QUERY_PARAM_NAME_HIDE_USER_TYPE]: 'y' } : {}),
    //     })}`;
    //     history.replace(newUrlPath);

    //     const provider = getFirebaseTwitterAuthProvider();
    //     auth.signInWithRedirect(provider);
    // }, [auth, urlSearchParams, accountType, currentLocationPathname, hideEmp, hideJS, history]);

    const onReCaptchaChange = value => {
        formik.setFieldValue('reCaptcha', value, true);
    };

    const handleClickForgotPasswordLink = () => {
        history.push(`${BASE_URL}${PATH_FORGOT_PASSWORD}`);
    };

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        isLoginMode,
        setIsLoginMode,
        formik,
        accountType,
        handleClickForgotPasswordLink,
        errorMessage,
        handleCloseErrorMessageDialog,
        confirmCreateAccountTitle,
        confirmCreateAccountMessage,
        handleCloseConfirmCreateAccountDialog,
        handleClickConfirmCreateAccountDialogYes,
        isInvitationMode,
        history,
        isJobCenter,
        handleClickContinueWithGoogleButton,
        handleClickContinueWithAppleButton,
        // handleClickContinueWithFacebookButton,
        // handleClickContinueWithTwitterButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Fragment>
            <Dialog {...defns.root} {...otherProps} classes={{ paperScrollPaper: paperScrollPaperCls }} fullScreen={smMatches}>
                <DialogTitle>
                    <div {...defns.titleContainer}>
                        {/* Put title on second line to fully utilize the space */}
                        <div></div>
                        <div {...defns.closeIconButtonContainer}>
                            <IconButton {...defns.closeIconButton}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>
                    <div {...defns.titleText}>{titleText}</div>
                </DialogTitle>
                <DialogContent {...defns.dialogContent} ref={setContentRef}>
                    <Grid {...defns.modalContainer}>
                        <Grid {...defns.modalContent}>
                            {/* continue with social media service  */}
                            <AfitdButton variant="contained" startIcon={<AppleIcon {...defns.appleIcon} />} {...defns.appleButton}>
                                <span {...defns.appleButtonLabelText}> Continue with Apple</span>
                            </AfitdButton>

                            {/* <AfitdButton variant="contained" startIcon={<img src={fbIcon} alt="" />} {...defns.facebookButton}>
                                <span {...defns.facebookButtonLabelText}> Continue with Facebook</span>
                            </AfitdButton> */}

                            {/* <AfitdButton variant="contained" startIcon={<img src={twitterIcon} alt="" />} {...defns.twitterButton}>
                                <span {...defns.twitterButtonLabelText}> Continue with Twitter</span>
                            </AfitdButton> */}

                            <AfitdButton variant="contained" color="default" startIcon={<img src={googleIcon} alt="" />} {...defns.googleButton}>
                                <span {...defns.googleButtonLabelText}> Continue with Google</span>
                            </AfitdButton>
                            {/* continue with social media service end */}

                            <Grid {...defns.createWithEmailContainer}>
                                <Grid item md={3} {...defns.createWithEmailItemLeft}>
                                    <hr />
                                </Grid>
                                <Grid item md={6} {...defns.createWithEmailItem2}>
                                    {isLoginMode ? <p {...defns.createWithEmailText}>or log in with email</p> : <p {...defns.createWithEmailText}>or create account with email</p>}
                                </Grid>
                                <Grid item md={3} {...defns.createWithEmailItemRight}>
                                    <hr />
                                </Grid>
                            </Grid>

                            {/* input form section */}
                            <Container {...defns.formContainer}>
                                <Grid {...defns.formMainGrid}>
                                    <Grid {...defns.formMainGridItem}>
                                        <form {...defns.loginForm}>
                                            {isLoginMode ? null : (
                                                <Grid {...defns.formInputContainer}>
                                                    <Grid {...defns.firstNameContainer}>
                                                        <label {...defns.fieldLabel} htmlFor="firstName">
                                                            First name
                                                        </label>
                                                        <br />
                                                        <AfitdTextField placeholder="Ex. Jack" id="firstName" {...defns.firstName} />
                                                    </Grid>

                                                    <Grid {...defns.lastNameContainer}>
                                                        <label {...defns.fieldLabel} htmlFor="lastName">
                                                            Last name
                                                        </label>
                                                        <AfitdTextField placeholder="Ex. Sparrow" id="lastName" {...defns.lastName} />
                                                    </Grid>
                                                </Grid>
                                            )}

                                            <Grid>
                                                <label {...defns.fieldLabel} htmlFor="email">
                                                    Email Address
                                                </label>
                                                <AfitdTextField {...defns.email} />
                                            </Grid>

                                            <Grid>
                                                <label {...defns.fieldLabel} htmlFor="password">
                                                    Password
                                                </label>
                                                <AfitdTextField {...defns.password} />
                                                <AfitdLink {...defns.forgotPasswordLink} label="Forgot Password?" />
                                            </Grid>
                                            {isLoginMode ? (
                                                <div style={{ marginTop: 24 }}>
                                                    <>
                                                        <Grid {...defns.reCaptchaContainer}>
                                                            <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onReCaptchaChange} size="normal" />
                                                        </Grid>
                                                        {Boolean(formik.errors.reCaptcha) && <AfitdTypography {...defns.reCaptchaError}>{formik.errors.reCaptcha}</AfitdTypography>}
                                                    </>
                                                    <div {...defns.loginButtonContainer}>
                                                        <AfitdButton {...defns.loginButton}>Log In</AfitdButton>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <Grid>
                                                        <label {...defns.fieldLabel} htmlFor="confirmPassword">
                                                            Confirm Password
                                                        </label>
                                                        <AfitdTextField {...defns.passwordConfirm} />
                                                    </Grid>
                                                    <Grid {...defns.checkBoxContainer}>
                                                        <FormControl component="fieldset">
                                                            <RadioGroup aria-label="report" name="report" value={accountType} onChange={handleChangeAccountType}>
                                                                <div {...defns.radioContainer}>
                                                                    {!hideJS && (
                                                                        <>
                                                                            <FormControlLabel control={<Radio />} {...defns.userTypeJsRadioButton} />
                                                                            <br />
                                                                        </>
                                                                    )}
                                                                    {!hideEmp && <FormControlLabel control={<Radio />} {...defns.userTypeEmpRadioButton} />}
                                                                </div>
                                                            </RadioGroup>
                                                        </FormControl>
                                                    </Grid>
                                                    <div style={{ marginTop: 24 }}>
                                                        <>
                                                            <Grid {...defns.reCaptchaContainer}>
                                                                <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onReCaptchaChange} />
                                                            </Grid>
                                                            {Boolean(formik.errors.reCaptcha) && (
                                                                <AfitdTypography {...defns.reCaptchaError}>{formik.errors.reCaptcha}</AfitdTypography>
                                                            )}
                                                        </>
                                                    </div>
                                                    <div {...defns.createAccountButtonContainer}>
                                                        <AfitdButton {...defns.createAccountButton}>Create Account</AfitdButton>
                                                    </div>
                                                </>
                                            )}
                                        </form>
                                    </Grid>
                                </Grid>
                            </Container>
                            {/* input form section end */}

                            {isLoginMode ? (
                                <div {...defns.privacyPolicyContainer}>
                                    <AfitdTypography {...defns.privacyPolicyText}>
                                        Don’t have an account? <AfitdLink {...defns.createAccountLink}>Create account</AfitdLink>
                                    </AfitdTypography>
                                </div>
                            ) : (
                                <>
                                    {!noLogin && (
                                        <div {...defns.alreadyAcContainer}>
                                            <AfitdLink {...defns.alreadyAcText}>I already have an account</AfitdLink>
                                        </div>
                                    )}
                                </>
                            )}
                            <div {...defns.privacyPolicyContainer}>
                                <AfitdTypography {...defns.privacyPolicyText}>
                                    By {!isLoginMode ? 'creating an account' : 'logging in'}, you agree to our{' '}
                                    <AfitdLink {...defns.termsConditionLink}>Terms and Conditions</AfitdLink> and <AfitdLink {...defns.privacyPolicyLink}>Privacy Policy</AfitdLink>
                                </AfitdTypography>
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>{dialogActions}</DialogActions>
            </Dialog>
            {!!errorMessage && <AfitdMessageDialog {...defns.errorMessageDialog} />}
            {!!confirmCreateAccountMessage && <AfitdConfirmDialog {...defns.confirmCreateAccountDialog} />}
        </Fragment>
    );
};

export default LoginDialog;
